import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Button,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Edit,
} from '@mui/icons-material';
import {
  getPartnerProducts,
  enableProduct,
  disableProduct,
  updateProductPricing,
  validatePrice,
  PricingUpdate,
} from '../../services/api/partnerService';
import { PartnerProductListResponse, PartnerProduct } from '../../types';

const formatCurrency = (amount: number): string => {
  return `¥${amount.toLocaleString()}`;
};

const PartnerProductsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<PartnerProductListResponse | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // 価格設定ダイアログ
  const [pricingDialog, setPricingDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<PartnerProduct | null>(null);
  const [pricingForm, setPricingForm] = useState<{ [planCode: string]: number }>({});
  const [pricingErrors, setPricingErrors] = useState<{ [planCode: string]: string }>({});
  const [savingPricing, setSavingPricing] = useState(false);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPartnerProducts();
      setProducts(data);
    } catch (err: any) {
      console.error('Products load error:', err);
      setError(err.response?.data?.detail || '商品の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProduct = async (product: PartnerProduct) => {
    setActionLoading(product.id);
    try {
      if (product.is_enabled) {
        await disableProduct(product.id);
      } else {
        await enableProduct(product.id);
      }
      await loadProducts();
    } catch (err: any) {
      console.error('Toggle error:', err);
      setError(err.response?.data?.detail || '操作に失敗しました');
    } finally {
      setActionLoading(null);
    }
  };

  const handleOpenPricing = (product: PartnerProduct) => {
    setSelectedProduct(product);
    const initialPricing: { [key: string]: number } = {};
    product.plans.forEach((plan) => {
      const customPrice = product.custom_pricing?.[plan.code]?.price;
      initialPricing[plan.code] = customPrice ?? product.base_prices[plan.code] ?? 0;
    });
    setPricingForm(initialPricing);
    setPricingErrors({});
    setPricingDialog(true);
  };

  const handlePriceChange = async (planCode: string, value: string) => {
    const price = parseInt(value) || 0;
    setPricingForm({ ...pricingForm, [planCode]: price });

    // バリデーション
    try {
      const result = await validatePrice(price);
      if (!result.valid) {
        setPricingErrors({ ...pricingErrors, [planCode]: result.message });
      } else {
        const newErrors = { ...pricingErrors };
        delete newErrors[planCode];
        setPricingErrors(newErrors);
      }
    } catch (err) {
      // Silent fail
    }
  };

  const handleSavePricing = async () => {
    if (!selectedProduct) return;

    // エラーチェック
    if (Object.keys(pricingErrors).length > 0) {
      return;
    }

    setSavingPricing(true);
    try {
      const pricing: PricingUpdate = {};
      Object.entries(pricingForm).forEach(([planCode, price]) => {
        pricing[planCode] = { price };
      });

      await updateProductPricing(selectedProduct.id, pricing);
      setPricingDialog(false);
      await loadProducts();
    } catch (err: any) {
      console.error('Save pricing error:', err);
      setError(err.response?.data?.detail || '価格設定の保存に失敗しました');
    } finally {
      setSavingPricing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadProducts}>
          再読み込み
        </Button>
      </Box>
    );
  }

  if (!products) return null;

  return (
    <Box sx={{ p: 3 }}>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            商品管理
          </Typography>
          <Typography variant="body2" color="text.secondary">
            販売する商品を選択し、価格を設定してください
          </Typography>
        </Box>
        <Chip
          label={`${products.enabled_count} / ${products.max_products} 商品`}
          color={products.enabled_count >= products.max_products ? 'error' : 'primary'}
        />
      </Box>

      {/* 商品上限警告 */}
      {products.enabled_count >= products.max_products && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          商品数の上限に達しています。新しい商品を有効化するには、既存の商品を無効化してください。
        </Alert>
      )}

      {/* 商品リスト */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>商品</TableCell>
              <TableCell>プラン</TableCell>
              <TableCell>基本価格</TableCell>
              <TableCell>設定価格</TableCell>
              <TableCell align="center">有効</TableCell>
              <TableCell align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.items.map((product) => (
              <TableRow key={product.id}>
                <TableCell>
                  <Box>
                    <Typography fontWeight="bold">{product.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {product.code}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {product.plans.map((plan) => (
                    <Chip
                      key={plan.code}
                      label={plan.name}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </TableCell>
                <TableCell>
                  {product.plans.map((plan) => (
                    <Box key={plan.code} sx={{ mb: 0.5 }}>
                      <Typography variant="body2">
                        {plan.name}: {formatCurrency(product.base_prices[plan.code] ?? 0)}
                      </Typography>
                    </Box>
                  ))}
                </TableCell>
                <TableCell>
                  {product.plans.map((plan) => {
                    const customPrice = product.custom_pricing?.[plan.code]?.price;
                    const basePrice = product.base_prices[plan.code] ?? 0;
                    const displayPrice = customPrice ?? basePrice;
                    const isCustom = customPrice !== undefined && customPrice !== basePrice;
                    return (
                      <Box key={plan.code} sx={{ mb: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isCustom ? 'primary.main' : 'text.primary',
                            fontWeight: isCustom ? 'bold' : 'normal',
                          }}
                        >
                          {plan.name}: {formatCurrency(displayPrice)}
                          {isCustom && ' (カスタム)'}
                        </Typography>
                      </Box>
                    );
                  })}
                </TableCell>
                <TableCell align="center">
                  <Switch
                    checked={product.is_enabled}
                    onChange={() => handleToggleProduct(product)}
                    disabled={
                      actionLoading === product.id ||
                      (!product.is_enabled && products.enabled_count >= products.max_products)
                    }
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="価格設定">
                    <IconButton
                      onClick={() => handleOpenPricing(product)}
                      disabled={!product.is_enabled}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 価格設定ダイアログ */}
      <Dialog open={pricingDialog} onClose={() => setPricingDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          価格設定 - {selectedProduct?.name}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            価格は¥0（無料）または¥50以上で設定してください。¥1〜¥49はStripeの最低金額制限により設定できません。
          </Alert>
          {selectedProduct?.plans.map((plan) => (
            <Box key={plan.code} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                {plan.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                基本価格: {formatCurrency(selectedProduct.base_prices[plan.code] ?? 0)}
              </Typography>
              <TextField
                fullWidth
                type="number"
                label="販売価格"
                value={pricingForm[plan.code] ?? 0}
                onChange={(e) => handlePriceChange(plan.code, e.target.value)}
                error={!!pricingErrors[plan.code]}
                helperText={pricingErrors[plan.code]}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>¥</Typography>,
                }}
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPricingDialog(false)}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleSavePricing}
            disabled={savingPricing || Object.keys(pricingErrors).length > 0}
          >
            {savingPricing ? <CircularProgress size={20} /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerProductsPage;
